import React, { useEffect, useState, useRef } from "react";
import { X, Atom, ShieldCheck } from "@phosphor-icons/react";
import api from "@/lib/api";

/* Minimal markdown renderer: ## headers, - bullets, **bold**, paragraphs */
function renderInline(text, key) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <span key={key}>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? (
          <strong key={i} className="text-white font-semibold">{p.slice(2, -2)}</strong>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        )
      )}
    </span>
  );
}

function Markdown({ text }) {
  const lines = text.split("\n");
  const blocks = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="space-y-2 my-3">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-3 text-white/75 text-sm leading-relaxed">
              <span className="text-cyan-400 mt-1">▹</span>{renderInline(b, i)}
            </li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (line.startsWith("## ")) {
      flush();
      blocks.push(<h3 key={`h-${idx}`} className="text-tel text-[10px] text-cyan-400/90 mt-7 mb-2">{line.slice(3)}</h3>);
    } else if (line.startsWith("- ")) {
      bullets.push(line.slice(2));
    } else if (line.trim() === "") {
      flush();
    } else {
      flush();
      blocks.push(<p key={`p-${idx}`} className="text-white/75 text-sm leading-relaxed my-2">{renderInline(line, idx)}</p>);
    }
  });
  flush();
  return <div>{blocks}</div>;
}

export default function DeepDive({ object, onClose }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!object || started.current) return;
    started.current = true;
    (async () => {
      try {
        const res = await api.get(`/objects/${object.id}/analysis`);
        setText(res.data.content || "");
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [object]);

  if (!object) return null;

  return (
    <div data-testid="deepdive-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
      style={{ background: "rgba(3,3,5,0.85)", backdropFilter: "blur(10px)" }} onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[90vh] glass rounded-3xl overflow-hidden flex flex-col fade-up" onClick={(e) => e.stopPropagation()}>
        {/* hero image */}
        <div className="relative h-56 sm:h-72 flex-shrink-0">
          {object.image ? (
            <img src={object.image} alt={object.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full" style={{ background: `radial-gradient(circle at 50% 40%, ${object.color}55, #05060a)` }} />
          )}
          <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(10,10,15,1) 0%, transparent 60%)" }} />
          <button data-testid="deepdive-close" onClick={onClose} className="absolute right-5 top-5 w-10 h-10 rounded-full flex items-center justify-center text-white/80 hover:text-white transition-colors" style={{ background: "rgba(3,3,5,0.5)" }}>
            <X size={22} />
          </button>
          <div className="absolute bottom-5 left-7 right-7">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: object.color, boxShadow: `0 0 12px ${object.color}` }} />
              <span className="text-tel text-[9px] text-cyan-400/80">Astrophysical Analysis</span>
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">{object.name}</h2>
          </div>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto px-7 sm:px-10 py-6">
          <div className="flex items-center gap-2 mb-4">
            <Atom size={14} weight="duotone" color="#00D8FF" />
            <span className="text-tel text-[9px] text-white/45">Grounded in verified astronomy</span>
          </div>
          {loading ? (
            <div data-testid="analysis-loading" className="flex items-center gap-3 text-white/50 text-sm py-10">
              <span className="w-2 h-2 rounded-full bg-cyan-400 pulse-dot" /> Compiling the analysis…
            </div>
          ) : error ? (
            <div data-testid="analysis-error" className="text-white/60 text-sm py-10">
              Could not compile the analysis right now. Please close and try again.
            </div>
          ) : (
            <div data-testid="analysis-content"><Markdown text={text} /></div>
          )}
        </div>

        <div className="px-7 sm:px-10 py-4 border-t border-white/10 flex items-center gap-2">
          <ShieldCheck size={14} weight="duotone" color="#71717A" />
          <span className="text-tel text-[8px] text-white/30">Educational content · Verify critical facts independently</span>
        </div>
      </div>
    </div>
  );
}
